import { isSodimacStandalone } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import noop from '../../../../../../utils/noop';
import { YoutubeVideoStyles } from './YoutubeVideo.style';
import {
  cleanupTouchGuards,
  extractVideoId,
  getYoutubeParams,
  initYoutubePlayer,
  logError,
  setupTouchGuards,
  stopVideo,
} from './helpers';

const YoutubeVideo = forwardRef(({ url, setVideoPlaying, isMobile = false, store }, ref) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  useImperativeHandle(ref, () => ({
    stopVideo: () => {
      stopVideo(iframeRef.current);
      setVideoPlaying(false);
    },
  }));

  useEffect(() => {
    setVideoPlaying(true);

    if (iframeRef.current) {
      try {
        initYoutubePlayer(iframeRef.current, setVideoPlaying);
      } catch (error) {
        logError('Error starting YouTube player:', error);
      }
    }

    if (isMobile && containerRef.current) {
      setupTouchGuards(containerRef.current);
    }

    return () => {
      setVideoPlaying(false);
      if (isMobile && containerRef.current) {
        cleanupTouchGuards(containerRef.current);
      }
    };
  }, [url, setVideoPlaying, isMobile]);

  const videoId = extractVideoId(url);
  const youtubeParams = getYoutubeParams(isMobile, videoId, store);
  const videoClassName = isMobile ? 'youtube-video-mobile' : 'youtube-video';
  const disablePointer = !isSodimacStandalone(store) ? 'pointer-events-none' : '';

  return (
    <>
      <div className={`${videoClassName} ${disablePointer}`} ref={containerRef}>
        <iframe
          ref={iframeRef}
          title="Video"
          src={`${url}${youtubeParams}`}
          frameBorder="0"
          allowFullScreen
          allow="autoplay; fullscreen"
          loading="lazy"
        />
      </div>
      <style jsx>{YoutubeVideoStyles}</style>
    </>
  );
});

YoutubeVideo.displayName = 'YoutubeVideo';

YoutubeVideo.defaultProps = {
  url: '',
  setVideoPlaying: noop,
  isMobile: false,
  store: '',
};

YoutubeVideo.propTypes = {
  url: PropTypes.string,
  setVideoPlaying: PropTypes.func,
  isMobile: PropTypes.bool,
  store: PropTypes.string,
};

export default YoutubeVideo;
export { YoutubeVideo };
